<script setup lang="ts">
import { computed, ref } from 'vue'
import { Badge, Expandable } from '@hantera/design-system'

interface InheritedPermission {
  permission: string
  source: string
}

interface PermissionGroup {
  id: string
  name: string
  expanded: boolean
  permissions: Array<{
    id: string
    label: string
  }>
}

const props = withDefaults(
  defineProps<{
    modelValue: string[]
    searchQuery?: string
    inheritedPermissions?: InheritedPermission[]
    readonly?: boolean
  }>(),
  {
    searchQuery: '',
    readonly: false,
    inheritedPermissions: () => [],
  },
)

const emit = defineEmits<{
  'update:modelValue': [permissions: string[]]
  'update:searchQuery': [query: string]
}>()
const customPermissionInput = ref('')

const permissionGroups: PermissionGroup[] = [
  {
    id: 'iam',
    name: 'Identity & Access Management',
    expanded: true,
    permissions: [
      { id: 'iam:*', label: 'All IAM Operations' },
      { id: 'iam/principals:read', label: 'View Users' },
      { id: 'iam/principals:write', label: 'Manage Users' },
      { id: 'iam/principals:delete', label: 'Delete Users' },
      { id: 'iam/clients:read', label: 'View External Applications' },
      { id: 'iam/clients:write', label: 'Manage External Applications' },
      { id: 'iam/clients:delete', label: 'Delete External Applications' },
      { id: 'iam/roles:read', label: 'View Roles' },
      { id: 'iam/roles:write', label: 'Manage Roles' },
      { id: 'iam/roles:delete', label: 'Delete Roles' },
      { id: 'iam/sessions:read', label: 'View Sessions' },
      { id: 'iam/sessions:write', label: 'Manage Sessions' },
    ],
  },
  {
    id: 'graph',
    name: 'Graph',
    expanded: false,
    permissions: [
      { id: 'graph:*', label: 'All Graph Access' },
      { id: 'graph/order:*', label: 'Orders' },
      { id: 'graph/delivery:*', label: 'Deliveries' },
      { id: 'graph/invoice:*', label: 'Invoices' },
      { id: 'graph/payment:*', label: 'Payments' },
      { id: 'graph/customer:*', label: 'Customers' },
    ],
  },
  {
    id: 'actors',
    name: 'Actors',
    expanded: false,
    permissions: [
      { id: 'actors:*', label: 'All Actor Operations' },
      { id: 'actors/order:*', label: 'Order Actor' },
      { id: 'actors/payment:*', label: 'Payment Actor' },
      { id: 'actors/ticket:*', label: 'Ticket Actor' },
      { id: 'actors/asset:*', label: 'Asset Actor' },
    ],
  },
  {
    id: 'registry',
    name: 'Registry',
    expanded: false,
    permissions: [
      { id: 'registry:*', label: 'All Registry Access' },
      { id: 'registry:read', label: 'Read Registry' },
      { id: 'registry:write', label: 'Write Registry' },
    ],
  },
  {
    id: 'components',
    name: 'Components',
    expanded: false,
    permissions: [
      { id: 'components:*', label: 'All Components Access' },
      { id: 'components:read', label: 'View Components' },
      { id: 'components:write', label: 'Manage Components' },
    ],
  },
  {
    id: 'rules',
    name: 'Rules',
    expanded: false,
    permissions: [
      { id: 'rules:*', label: 'All Rules Access' },
      { id: 'rules:read', label: 'View Rules' },
      { id: 'rules:write', label: 'Manage Rules' },
    ],
  },
  {
    id: 'apps',
    name: 'Apps',
    expanded: false,
    permissions: [
      { id: 'apps:*', label: 'All Apps Access' },
      { id: 'apps:read', label: 'View Apps' },
      { id: 'apps:write', label: 'Install Apps' },
    ],
  },
  {
    id: 'jobs',
    name: 'Jobs',
    expanded: false,
    permissions: [
      { id: 'jobs:*', label: 'All Jobs Access' },
      { id: 'jobs:read', label: 'View Jobs' },
      { id: 'jobs:write', label: 'Manage Jobs' },
      { id: 'jobs:schedule', label: 'Schedule Jobs' },
      { id: 'jobs:statistics', label: 'View Job Statistics' },
    ],
  },
  {
    id: 'job-definitions',
    name: 'Job Definitions',
    expanded: false,
    permissions: [
      { id: 'job-definitions:*', label: 'All Job Definitions Access' },
      { id: 'job-definitions:read', label: 'View Job Definitions' },
      { id: 'job-definitions:write', label: 'Manage Job Definitions' },
    ],
  },
  {
    id: 'ingresses',
    name: 'Ingresses',
    expanded: false,
    permissions: [
      { id: 'ingresses:*', label: 'All Ingress Access' },
      { id: 'ingresses:read', label: 'View Ingresses' },
      { id: 'ingresses:write', label: 'Manage Ingresses' },
      { id: 'ingresses:http', label: 'Call HTTP Ingresses' },
      { id: 'ingresses:statistics', label: 'View Ingress Statistics' },
    ],
  },
  {
    id: 'egresses',
    name: 'Egresses',
    expanded: false,
    permissions: [
      { id: 'egresses:*', label: 'All Egress Access' },
      { id: 'egresses:read', label: 'View Egresses' },
      { id: 'egresses:write', label: 'Manage Egresses' },
      { id: 'egresses:invoke', label: 'Invoke Egresses' },
      { id: 'egresses:statistics', label: 'View Egress Statistics' },
    ],
  },

  {
    id: 'traces',
    name: 'Traces',
    expanded: false,
    permissions: [
      { id: 'traces:*', label: 'All Traces Access' },
      { id: 'traces:read', label: 'View Traces' },
    ],
  },

  {
    id: 'sendings',
    name: 'Sendings',

    expanded: false,
    permissions: [
      { id: 'sendings:*', label: 'All Sendings Access' },
      { id: 'sendings:read', label: 'View Sendings' },
      { id: 'sendings:write', label: 'Create and Manage Sendings' },
    ],
  },
  {
    id: 'files',
    name: 'Files',
    expanded: false,
    permissions: [
      { id: 'files:*', label: 'All Files Access' },
      { id: 'files:read', label: 'View Files' },
      { id: 'files:write', label: 'Manage Files' },
    ],
  },
  {
    id: 'me',
    name: 'Me',
    expanded: false,
    permissions: [
      { id: 'me:*', label: 'Full Access' },
      { id: 'me/profile:read', label: 'Read Profile' },
      { id: 'me/profile:write', label: 'Update Profile' },
      { id: 'me/settings:read', label: 'Read Settings' },
      { id: 'me/settings:write', label: 'Update Settings' },
      { id: 'me/pat:read', label: 'View PATs' },
      { id: 'me/pat:write', label: 'Manage PATs' },
    ],
  },
]

const customPermissions = computed(() => {
  return props.modelValue.filter((p) => !isInPredefinedGroups(p))
})

const hasInheritedPermissions = computed(() => {
  return props.inheritedPermissions && props.inheritedPermissions.length > 0
})

const filteredPermissionGroups = computed(() => {
  if (!props.searchQuery) return permissionGroups

  const query = props.searchQuery.toLowerCase()

  return permissionGroups
    .map((group) => ({
      ...group,
      permissions: group.permissions.filter(
        (p) =>
          p.id.toLowerCase().includes(query) || p.label.toLowerCase().includes(query)
      ),
    }))
    .filter((group) => group.permissions.length > 0)
})

function isChecked(permissionId: string): boolean {
  return props.modelValue.includes(permissionId) || isInherited(permissionId)
}

function isInherited(permissionId: string): boolean {
  return props.inheritedPermissions?.some((p) => p.permission === permissionId) || false
}

function getInheritedSource(permissionId: string): string | undefined {
  return props.inheritedPermissions?.find((p) => p.permission === permissionId)?.source
}

function getGroupTag(group: PermissionGroup): string | undefined {

  if (isChecked(`${group.id}:*`)) {
    return 'All'
  }

  return `${group.permissions.filter(g => isChecked(g.id)).length} / ${group.permissions.length}`
}

function togglePermission(permissionId: string) {
  if (props.readonly || isInherited(permissionId)) return

  const current = [...props.modelValue]
  const index = current.indexOf(permissionId)

  if (index > -1) {
    current.splice(index, 1)
  } else {
    current.push(permissionId)
  }

  emit('update:modelValue', current)
}

function addCustomPermission() {
  const permission = customPermissionInput.value.trim()

  if (!permission) return

  const pattern = /^[a-z0-9_\.\/-]+:[a-z0-9_*\/\.:-]+$/i
  if (!pattern.test(permission)) {
    return
  }

  if (props.modelValue.includes(permission)) {
    return
  }

  emit('update:modelValue', [...props.modelValue, permission])
  customPermissionInput.value = ''
}

function removeCustomPermission(permission: string) {
  const current = props.modelValue.filter((p) => p !== permission)
  emit('update:modelValue', current)
}

function isInPredefinedGroups(permission: string): boolean {
  return permissionGroups.some((g) => g.permissions.some((p) => p.id === permission))
}
</script>

<template>
  <div class="permission-picker">
    <!-- Pre-defined Permission Groups -->
    <div class="permission-groups">
      <div
        v-for="group in filteredPermissionGroups"
        :key="group.id"
        class="box p"
      >
        
      <Expandable :initially-expanded="group.expanded || (props.searchQuery?.length ?? 0) > 0">
        <template #label>
          <div class="group-label">
            <span>{{ group.name }}</span>
            <Badge>{{ getGroupTag(group) }}</Badge>
          </div>
        </template>
        <template #default>
          <ul class="permission-list box-content">
            <li v-for="perm in group.permissions" :key="perm.id" class="permission-item">
              <label>
                <input
                  type="checkbox"
                  :value="perm.id"
                  :checked="isChecked(perm.id)"
                  @change="togglePermission(perm.id)"
                  :disabled="readonly || isInherited(perm.id)"
                />
                <span class="permission-label">{{ perm.label }}</span>
                <span class="permission-id">{{ perm.id }}</span>
                <span v-if="isInherited(perm.id)" class="inherited-badge">
                  from {{ getInheritedSource(perm.id) }}
                </span>
              </label>
            </li>
          </ul>
        </template>
      </Expandable>
      
      </div>
    </div>

    <!-- Custom Permissions -->
    <div v-if="!readonly" class="box p">
      <Expandable :initially-expanded="customPermissions.length > 0">
        <template #label>
          <div class="group-label">
            <span>Custom Permissions</span>
            <Badge>{{ customPermissions.length }}</Badge>
          </div>
        </template>
        <template #default>
          <ul v-if="customPermissions.length > 0" class="custom-permission-list box-content p">
            <li v-for="perm in customPermissions" :key="perm" class="custom-permission-item box">
              <span class="custom-permission-label">{{ perm }}</span>
              <button @click="removeCustomPermission(perm)" class="btn-danger">Remove</button>
            </li>
          </ul>

          <form @submit.prevent="addCustomPermission" class="custom-permission-form">
            <input
              v-model="customPermissionInput"
              type="text"
              placeholder="resource/path:action"
              class="input"
            />
            <button type="submit" class="btn-primary">Add</button>
          </form>
        </template>
      </Expandable>
    </div>

    <!-- Inherited Permissions -->
    <div v-if="hasInheritedPermissions" class="inherited-permissions">
      <h4>Inherited from Roles</h4>
      <ul class="inherited-list">
        <li v-for="perm in inheritedPermissions" :key="perm.permission" class="inherited-item">
          <span class="permission-label">{{ perm.permission }}</span>
          <span class="role-badge">{{ perm.source }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.permission-picker {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.permission-groups {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.group-label {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.permission-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.permission-item label {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
}

.permission-item label:hover {
  background: rgba(0, 0, 0, 0.02);
}

.permission-item input[type='checkbox'] {
  cursor: pointer;
  margin: 0;
}

.permission-item input[type='checkbox']:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.permission-label {
  font-weight: 500;
  flex: 1;
}

.permission-id {
  font-size: 12px;
  color: var(--color-text-tertiary, #999);
  font-family: monospace;
}

.inherited-badge {
  font-size: 11px;
  color: var(--color-info, #0066cc);
  background: rgba(0, 102, 204, 0.1);
  padding: 2px 6px;
  border-radius: 3px;
  white-space: nowrap;
}

.custom-permissions {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--color-border);
}

.custom-permission-form {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.custom-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 14px;
  font-family: monospace;
}

.custom-permission-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.custom-permission-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
}

.custom-permission-label {
  font-family: monospace;
  font-size: 13px;
}

.inherited-permissions {
  padding: 10px;
  background: rgba(0, 102, 204, 0.05);
  border-left: 3px solid var(--color-info, #0066cc);
  border-radius: 4px;
}

.inherited-permissions h4 {
  margin: 0 0 10px 0;
  font-size: 13px;
  font-weight: 600;
}

.inherited-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.inherited-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  background: white;
  border-radius: 3px;
  font-size: 13px;
}

.role-badge {
  font-size: 11px;
  color: var(--color-info, #0066cc);
  padding: 2px 8px;
  border-radius: 3px;
  background: rgba(0, 102, 204, 0.1);
  white-space: nowrap;
}
</style>
